import { useEffect, useState } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import InputForm from "../InputForm/InputForm.jsx";
import InputPhone from "../InputPhone/InputPhone.jsx";
import useForm from "../../helpers/useForm.js";
import { getToken } from "../../services/mercadoPagoService.js";
import ButtonProcessing from "../ButtonProcessing/ButtonProcessing.jsx";
import {
  getTerms,
  payReservation,
  checkPayment,
} from "../../services/welletService.js";
import Money from "../Money/Money.jsx";
import ErrorBox from "../ErrorBox/ErrorBox.jsx";
import WarningBox from "../Common/WarningBox/WarningBox.jsx";
import Checkbox from "../Checkbox/Checkbox.jsx";
import { createSearchParams, useNavigate } from "react-router-dom";
import "../../css/selector.css";

import Modal from "../Modal/Modal.jsx";
import Header from "../Header/Header.jsx";
import { t } from "i18next";
import InputFormBorder from "../Common/InputFormBorder/InputFormBorder.jsx";

const StripeCheckoutForm = ({ token, reservation, showId }) => {
  const [btnStatus, setBtnStatus] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const [stripeToken, setStripeToken] = useState(null);
  const [stripeErrorMessage, setStripeErrorMessage] = useState("");
  const [warningMessage, setWarningMessage] = useState("");

  const [stripeReady, setStripeReady] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const validate = async (values) => {
    setBtnStatus("INPROGRESS");
    setStripeErrorMessage("");
    let errors = {};

    if (!values.customerName) {
      errors.customerName = t("error.name");
    }

    if (
      !values.email ||
      !/^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/g.test(values.email)
    ) {
      errors.email = t("error.email");
    }

    if (!values.customerName) {
      errors.customerName = t("error.name");
    }

    if (!acceptedTerms) errors.acceptedTerms = t("form.terms3");

    try {
      setStripeToken(null);
      const { error: submitError } = await elements.submit();
      if (submitError) {
        errors.stripeError = submitError;
      }
      else {
          if(!reservation.gateway.paymentIntentClientSecret) { // no need to tokenize card if payment intent is already created
            const { error, confirmationToken } = await stripe.createConfirmationToken({
              elements,
              params: {
                payment_method_data: {
                  billing_details: {
                    email: values.email
                  }
                }
              }
            });
            if (error) {
              errors.stripeError = error.message;
              setStripeErrorMessage(error.message);
            } else {
              setStripeToken(confirmationToken);
            }
        }
      }
    } catch (error) {
      console.error("Error creating confirmationToken:", error);
    }

    if (Object.keys(errors).length > 0) {
      setBtnStatus("NORMAL");
    }

    return errors;
  };

  const sendForm = async () => {
    setError("");
    setStripeErrorMessage("");

    const customer = {
      name: values.customerName,
      email: values.email,
      phone: values.phone,
    };

    try {
      if (reservation.gateway.paymentIntentClientSecret) {
        return await confirmStripePayment();
      }

      const stripeCard = stripeToken.payment_method_preview?.card;
      const card = {
        token: stripeToken.id,
        brand: stripeCard?.brand,
        number: "",
        cvc: "",
        expiryMonth: stripeCard?.exp_month || "",
        expiryYear: stripeCard?.exp_year || "",
        bin: "",
        country: stripeCard?.country,
        last4: stripeCard?.last4,
      };

      const pay = await payReservation(
        token,
        card,
        customer,
        reservation.remainingDepositAmount,
        1
      );
      if (pay.success) {
        setBtnStatus("FINISHED");
        setTimeout(() => {
          navigate({
            pathname: "/success",
            search: createSearchParams({ token: token }).toString(),
          });
        }, 1000);
      } else if (pay.status === "PENDING_CHALLENGE") {
        handleNextActions(pay.clientSecret, token, pay.chargeId);
      } else {
        setBtnStatus("NORMAL");
        setError(pay.error);
      }
    } catch (e) {
      setBtnStatus("NORMAL");
      if (e.response && e.response.data && e.response.data.errorCode)
        setError(e.response.data.errorCode);
      else if (e.code === "ERR_NETWORK" || e.code === "ECONNABORTED") {
        setError("ERR_INTERNET_DISCONNECTED");
      } else {
        setError("API_ERROR_UNKNOWN");
      }
    }
  };

  const confirmStripePayment = async () => {
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          //return_url: window.location.href,
          payment_method_data: {
            billing_details: {
              email: values.email,
            },
          },
        },
        redirect: "if_required",
      });

      if (error && error.message) {
        setBtnStatus("NORMAL");
        setStripeErrorMessage(error.message);
        return;
      }

      if (paymentIntent.status === "succeeded") {
        await awaitPaymentStatus(paymentIntent.id);
      }
      else {
        if(paymentIntent.next_action?.display_bank_transfer_instructions) {
          const updatedPaymentIntent = await stripe.retrievePaymentIntent(paymentIntent.client_secret);
          if(updatedPaymentIntent?.paymentIntent?.status === 'succeeded') {
            await awaitPaymentStatus(updatedPaymentIntent.paymentIntent.id);
          }
          else {
            setWarningMessage(t('paymentBankTransferWarning'));
          }
        }
        setBtnStatus("NORMAL");
      }
    } catch (e) {
      console.error("Error confirming payment:", e);
    }
  };

  const awaitPaymentStatus = async (paymentIntentId) => {
    const pay = await checkPayment(token, paymentIntentId);

    if (pay.success && pay.status === "SUCCESS") {
      setBtnStatus("FINISHED");
      navigate({
        pathname: "/success",
        search: createSearchParams({ token: token }).toString(),
      });
    } else {
      setBtnStatus("NORMAL");
      if (pay.error) {
        setError(pay.error);
      } else {
        setError("GENERIC_ERROR");
      }
    }
  };

  const handleNextActions = async (clientSecret, orderToken, chargeId) => {
    const { error, paymentIntent } = await stripe.handleNextAction({
      clientSecret: clientSecret,
    });

    if (error && error.message) {
      setBtnStatus("NORMAL");
      setStripeErrorMessage(error.message);

      // send payment to server, so we save the payment detail with the error
      await checkPayment(orderToken, chargeId);
      return;
    }

    const pay = await checkPayment(orderToken, chargeId);

    if (pay.success && pay.status === "SUCCESS") {
      setBtnStatus("FINISHED");
      navigate({
        pathname: "/success",
        search: createSearchParams({ token: token }).toString(),
      });
    } else {
      setBtnStatus("NORMAL");
      if (pay.error) {
        setError(pay.error);
      } else {
        setError("GENERIC_ERROR");
      }
    }
  };

  const { values, errors, handleChange, handleSubmit } = useForm(
    sendForm,
    validate
  );

  const handleCheck = (event) => {
    if (event.target.checked) {
      setAcceptedTerms(true);
      errors.acceptedTerms = null;
    } else {
      setAcceptedTerms(false);
    }
  };

  useEffect(() => {
    setDefaultValues();
  }, []);

  const setDefaultValues = () => {
    values.customerName = reservation.customerName;
    values.email = reservation.customerEmail;
    values.phone = reservation.customerPhone;
  };

  const handleTerms = (e) => {
    e.preventDefault();
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const renderWarningBox = () => {
    if (warningMessage) {
      return (
        <div className="mt-4">
          <WarningBox title={"Atención"} subtitle={warningMessage} />
        </div>
      );
    }

    return null;
  };

  const renderErrorBox = () => {
    if (stripeErrorMessage) {
      return (
        <div className="mt-4">
          <ErrorBox
            title={t("paymentErrors.title")}
            description={stripeErrorMessage}
          />
        </div>
      );
    }

    if (error !== "") {
      return (
        <div className="mt-4">
          <ErrorBox
            title={t("paymentErrors.title")}
            description={t("paymentErrors." + error)}
          />
        </div>
      );
    }

    return null;
  };

  const renderPurchaseButton = () => {
    if (!stripeReady) return null;

    return (
      <div className="mt-4">
        <div>
          <ButtonProcessing
            status={btnStatus}
            text={t("form.payButton")}
            amount={<Money value={reservation.remainingDepositAmount} />}
            inProgressText={t("form.processing")}
            size="large"
            type="submit"
            onClick={handleSubmit}
            arrow
            color="#000"
            backgroundColor="var(--color-primary)"
            iconColor="#000"
          />
        </div>
      </div>
    );
  };

  const stripeOptions = {
    fields: {
      billingDetails: {
        email: "never",
      },
    },
    layout: {
      type: "tabs",
      defaultCollapsed: false,
    },
  };

  return (
    <>
      <form id="form-checkout" onSubmit={handleSubmit}>
        <div className="mt-4">
          <div className="fw-semibold text-primary font-tiny">
            {t("customerInformation")}
          </div>
          <div className="mt-3">
            <InputFormBorder
              name={"customerName"}
              type={"text"}
              value={values.customerName}
              error={errors.customerName}
              placeholder={t("form.cardHolder")}
              onChange={handleChange}
              dataCheckout={"fullName"}
            />

            <InputFormBorder
              name={"email"}
              type={"email"}
              value={values.email}
              error={errors.email}
              icon="mail"
              placeholder={t("form.reservationEmail")}
              onChange={handleChange}
            />
          </div>

          <div className="fw-semibold text-primary font-tiny mt-4">
            {t("informationForPayment")}
          </div>
          <div className="mt-3">
            <PaymentElement
              options={stripeOptions}
              onReady={() => setStripeReady(true)}
            />
          </div>

          <div
            className="font-small text-center position-relative mt-4"
            style={{ paddingBottom: errors.acceptedTerms ? "27px" : "0" }}
          >
            <Checkbox
              label={
                <div>
                  {t("form.terms1")}{" "}
                  <u onClick={handleTerms}>{t("form.terms2")}</u>
                </div>
              }
              onChange={handleCheck}
            />
            {errors.acceptedTerms ? (
              <div className="errorLabel mt-1">{t("form.terms3")}</div>
            ) : null}
          </div>
        </div>
        {renderWarningBox()}
        {renderErrorBox()}
        {renderPurchaseButton()}
      </form>
      <Modal
        open={showModal}
        fullScreen
        showCloseButton={false}
        animationClass="slide-left-right"
        className={"terms-and-conditions"}
      >
        <Header onClick={handleModalClose} title={t("form.terms2")} />
        <div
          dangerouslySetInnerHTML={{ __html: reservation.termsAndConditions }}
          className="container"
        ></div>
      </Modal>
    </>
  );
};

export default StripeCheckoutForm;
